import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Share2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAppStore, useProjectsStore } from '../store';
import { T } from '../i18n';

export default function SaveModal({ onClose }) {
  const { lang, currentObject, configs, photoBase64, iaImage } = useAppStore();
  const { saveProject } = useProjectsStore();
  const t = T[lang] || T.fr;
  
  const [step, setStep] = useState(1); // 1: name input, 2: success
  const [name, setName] = useState('');
  const [savedProject, setSavedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t.name_required);
      return;
    }
    
    // Check if name starts with DRAFT
    if (name.toUpperCase().startsWith('DRAFT')) {
      toast.error('Le nom ne peut pas commencer par DRAFT');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create thumbnail from canvas
      let thumbnail = null;
      const canvas = document.querySelector('canvas');
      if (canvas) {
        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = 120;
        thumbCanvas.height = 90;
        const ctx = thumbCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, 120, 90);
        thumbnail = thumbCanvas.toDataURL('image/jpeg', 0.7);
      }
      
      const project = await saveProject({
        name: name.trim(),
        object_type: currentObject || 'store',
        config: configs[currentObject || 'store'],
        photo_base64: photoBase64,
        ia_image_url: iaImage,
        thumbnail
      });
      
      setSavedProject(project);
      setStep(2);
      toast.success(t.project_saved);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleShare = async () => {
    try {
      if (navigator.share && savedProject?.ia_image_url) {
        await navigator.share({
          title: savedProject.name,
          text: `Ma simulation ${savedProject.name} - SoView-FX`,
          url: savedProject.ia_image_url
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Lien copié !');
      }
    } catch {
      // Cancelled
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-28">
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="relative glass-light rounded-2xl p-6 w-[88vw] max-w-sm"
      >
        {step === 1 ? (
          // Step 1: Name input
          <>
            <h3 className="text-lg font-bold text-secondary text-center mb-4">
              {t.save_as}
            </h3>
            
            <input
              type="text"
              placeholder={t.project_name}
              value={name}
              onChange={(e) => setName(e.target.value.toUpperCase())}
              className="w-full input text-center uppercase tracking-wider font-bold"
              autoFocus
              maxLength={30}
              data-testid="project-name-input"
            />
            
            <div className="flex gap-3 mt-5">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-muted-foreground font-medium"
                data-testid="cancel-save"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleSave}
                disabled={loading || !name.trim()}
                className="flex-1 btn-primary disabled:opacity-50"
                data-testid="confirm-save"
              >
                {loading ? '...' : t.save}
              </button>
            </div>
          </>
        ) : (
          // Step 2: Success
          <>
            <div className="text-center">
              <p className="text-lg font-bold text-secondary">{t.project_saved}</p>
              <p className="text-white font-bold mt-1 tracking-wider">{savedProject?.name}</p>
            </div>
            
            <button
              onClick={handleShare}
              className="w-full btn-primary mt-5 flex items-center justify-center gap-2"
              data-testid="share-saved"
            >
              <Share2 size={18} />
              {t.share_project}
            </button>
            
            <button
              onClick={onClose}
              className="w-full mt-3 py-3 rounded-xl bg-white/5 border border-white/10 text-muted-foreground font-medium"
              data-testid="back-to-project"
            >
              {t.back_to_project}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
